import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { DraggableGradeBoundaries } from '../Grade/DraggableGradeBoundaries';
import { PreviewModal } from '../PreviewModal/PreviewModal';
import { EditProductModal } from './ProductRegistration/EditProductModal';
import { PlusIcon, TrashIcon } from '../Icons/Icons';
import { useEvaluationStore } from '../../store/evaluationStore';
import type { GradeSetting } from '../../types/evaluation';

export const GradeAssignment: React.FC = () => {
  const { t } = useTranslation();
  const { products, criteria, setCurrentStep, getEvaluationPosition, setEvaluationPosition, setGrades, getGrades, saveDataAsync, grades } = useEvaluationStore();
  const [selectedCriterionIndex, setSelectedCriterionIndex] = React.useState(0); const [isGradeEditModalOpen, setIsGradeEditModalOpen] = React.useState(false);
  const [gradeSettings, setGradeSettings] = React.useState<GradeSetting[]>([
    { letter: 'A', xStart: 80, gradeLetter: 'A', gradeXStart: 80 },
    { letter: 'B', xStart: 60, gradeLetter: 'B', gradeXStart: 60 },
    { letter: 'C', xStart: 40, gradeLetter: 'C', gradeXStart: 40 },
    { letter: 'D', xStart: 0, gradeLetter: 'D', gradeXStart: 0 }
  ]);


  const [previewModal, setPreviewModal] = React.useState<{ isOpen: boolean; url: string; title: string }>({
    isOpen: false,
    url: '',
    title: ''
  });

  const [editingProductIndex, setEditingProductIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    // 現在の観点の等級設定を読み込み
    const currentGrades = getGrades(selectedCriterionIndex);
    if (currentGrades.length > 0) {
      // 降順でソートして設定
      const sortedGrades = [...currentGrades].sort((a, b) => b.gradeXStart - a.gradeXStart);
      setGradeSettings(sortedGrades.map(g => ({ letter: g.gradeLetter, xStart: g.gradeXStart, gradeLetter: g.gradeLetter, gradeXStart: g.gradeXStart })));
    } else {
      // デフォルト設定も降順でソート
      const defaultGrades = [
        { letter: 'A', xStart: 80, gradeLetter: 'A', gradeXStart: 80 },
        { letter: 'B', xStart: 60, gradeLetter: 'B', gradeXStart: 60 },
        { letter: 'C', xStart: 40, gradeLetter: 'C', gradeXStart: 40 },
        { letter: 'D', xStart: 0, gradeLetter: 'D', gradeXStart: 0 }
      ].sort((a, b) => b.xStart - a.xStart);
      setGradeSettings(defaultGrades);
      setGrades(selectedCriterionIndex, defaultGrades.map(g => ({ gradeLetter: g.letter, gradeXStart: g.xStart })));
    }
  }, [selectedCriterionIndex, getGrades, setGrades]);

  const handleGradeLetterChange = (index: number, value: string) => {
    const newGrades = [...gradeSettings];
    newGrades[index] = { 
      ...newGrades[index], 
      letter: value,
      gradeLetter: value // Update both fields
    };
    setGradeSettings(newGrades);
    setGrades(selectedCriterionIndex, newGrades.map(g => ({ gradeLetter: g.letter, gradeXStart: g.xStart })));
  };

  const handleBoundaryDrag = (index: number, newPosition: number) => {
    const newGrades = [...gradeSettings];

    // 境界位置を更新（制約チェック付き）
    const minPosition = index === gradeSettings.length - 1 ? 0 : gradeSettings[index + 1].xStart + 5;
    const maxPosition = index === 0 ? 95 : gradeSettings[index - 1].xStart - 5;

    const clampedPosition = Math.max(minPosition, Math.min(maxPosition, newPosition));
    newGrades[index] = { 
      ...newGrades[index], 
      xStart: clampedPosition,
      gradeXStart: clampedPosition // Update both fields
    };

    setGradeSettings(newGrades);
    // ストアにも同時に更新を反映
    setGrades(selectedCriterionIndex, newGrades.map(g => ({ 
      gradeLetter: g.letter, 
      gradeXStart: g.xStart 
    })));
  };

  const handleProductPositionChange = (productIndex: number, x: number, y: number) => {
    setEvaluationPosition(productIndex, selectedCriterionIndex, x, y);
  };

  const addGrade = () => {
    // 新しい等級を中間位置に追加
    const newPosition = gradeSettings.length > 1
      ? Math.round((gradeSettings[0].gradeXStart + gradeSettings[gradeSettings.length - 1].gradeXStart) / 2)
      : 50;

    // 自動的に等級名を生成 (A, B, C, ...)
    const existingLetters = gradeSettings.map(g => g.gradeLetter).filter(l => l.length > 0);
    let newLetter = 'A';
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = 0; i < alphabet.length; i++) {
      if (!existingLetters.includes(alphabet[i])) {
        newLetter = alphabet[i];
        break;
      }
    }

    const newGrade = { letter: newLetter, xStart: newPosition, gradeLetter: newLetter, gradeXStart: newPosition };
    const newGrades = [...gradeSettings, newGrade].sort((a, b) => b.xStart - a.xStart);
    setGradeSettings(newGrades);
    setGrades(selectedCriterionIndex, newGrades.map(g => ({ gradeLetter: g.letter, gradeXStart: g.xStart })));
  };

  const removeGrade = (index: number) => {
    if (gradeSettings.length > 1) {
      const newGrades = gradeSettings.filter((_, i) => i !== index);
      setGradeSettings(newGrades);
      setGrades(selectedCriterionIndex, newGrades.map(g => ({ gradeLetter: g.letter, gradeXStart: g.xStart })));
    }
  };

  const getProductGrade = (productIndex: number, criterionIndex: number): string => {
    const position = getEvaluationPosition(productIndex, criterionIndex);
    if (!position) return t('gradeAssignment.status.notEvaluated');

    const currentGrades = getGrades(criterionIndex);
    const sortedGrades = [...currentGrades].sort((a, b) => b.gradeXStart - a.gradeXStart);

    for (const grade of sortedGrades) {
      if (position.x >= grade.gradeXStart && grade.gradeLetter && grade.gradeLetter.trim().length > 0) {
        return grade.gradeLetter;
      }
    }

    const lastGrade = sortedGrades[sortedGrades.length - 1];
    return (lastGrade?.gradeLetter && lastGrade.gradeLetter.trim().length > 0) 
      ? lastGrade.gradeLetter 
      : t('gradeAssignment.status.notSet');
  };

  const handlePrev = async () => {
    await saveDataAsync();
    setCurrentStep(3);
  };
  const handleNext = async () => {
    if (canFinish) {
      await saveDataAsync();
      setCurrentStep(5); // ルーブリック生成へ進む
    }
  };
  const handleFinish = async () => {
    // 最終保存処理
    const { saveDataAsync } = useEvaluationStore.getState();
    const success = await saveDataAsync();

    if (success) {
      alert(t('gradeAssignment.saveSuccess'));
    }
  };

  const openDetailModal = (productIndex: number) => {
    const product = products[productIndex];
    if (product && product.reviewLink) {
      // プレビューリンクがある場合は直接プレビューを開く
      openPreview(product.reviewLink, product.reviewTitle);
    } else {
      // プレビューリンクがない場合は編集モーダルを開く
      setEditingProductIndex(productIndex);
    }
  };


  const openPreview = (url: string, title: string) => {
    setPreviewModal({
      isOpen: true,
      url,
      title
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: '',
      title: ''
    });
  };

  const canFinish = criteria.every(criterion => getGrades(criteria.indexOf(criterion)).length > 0);

  if (products.length === 0 || criteria.length === 0) {
    return (
      <section className="section">
        <div className="empty-state">
          <h2 className="empty-state__title">{t('gradeAssignment.emptyState.title')}</h2>
          <p className="empty-state__description">
            {t('gradeAssignment.emptyState.description')}
          </p>
          <Button
            variant="secondary"
            onClick={() => setCurrentStep(3)}
          >
            {t('gradeAssignment.prevButton')}
          </Button>
        </div>
      </section>
    );
  }

  const currentCriterion = criteria[selectedCriterionIndex];

  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{t('gradeAssignment.title')}</h2>
        <p className="section__description">
          {t('gradeAssignment.description')}
        </p>
      </div>

      <div className="grade-assignment">
        {/* 等級設定エリア */}
        <div className="grade-settings">

          {/* インタラクティブ境界設定 */}
          <div className="grade-boundaries-interactive">
            <div className="grade-boundaries-container">
              <DraggableGradeBoundaries
                gradeSettings={gradeSettings}
                products={products}
                criterionIndex={selectedCriterionIndex}
                getPosition={getEvaluationPosition}
                onBoundaryChange={handleBoundaryDrag}
                onProductDoubleClick={openDetailModal}
                onProductPositionChange={handleProductPositionChange}
              />
              
              {/* 等級を編集ボタン - 右下 */}
              <button
                className="grade-edit-button"
                onClick={() => setIsGradeEditModalOpen(true)}
                title={t('gradeAssignment.editGradesButton')}
              >
                {t('gradeAssignment.editGradesButton')}
              </button>
              
              {/* 観点選択 - 配置領域内下部 */}
              <div className="criterion-selector criterion-selector--inline">
                <span className="criterion-selector__label">{t('gradeAssignment.criterionSelector.title')}</span>
                <div className="criterion-tabs">
                  {criteria.map((criterion, index) => {
                    const grades = getGrades(index);
                    const hasGrades = grades.length > 0;
                    const progressPercentage = hasGrades ? 100 : 0;
                    
                    return (
                      <button
                        key={index}
                        className={`criterion-tab ${index === selectedCriterionIndex ? 'active' : ''} ${hasGrades ? 'sufficient' : ''}`}
                        onClick={() => setSelectedCriterionIndex(index)}
                      >
                        <span className="criterion-tab__title">{criterion.title}</span>
                        <div className="criterion-tab__progress-bar">
                          <div 
                            className="criterion-tab__progress-fill" 
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 等級分布表 */}
          <div className="grade-distribution">
            <h5 className="grade-distribution__title">{t('gradeAssignment.distribution.title')}</h5>
            <table className="grade-table">
              <thead>
                <tr>
                  <th>{t('gradeAssignment.table.grade')}</th>
                  <th>{t('gradeAssignment.table.count')}</th>
                  <th>{t('gradeAssignment.table.percentage')}</th>
                  <th>{t('gradeAssignment.table.subjects')}</th>
                </tr>
              </thead>
              <tbody>
                {gradeSettings.map((grade) => {
                  const gradedProducts = products.filter((_, productIndex) =>
                    getProductGrade(productIndex, selectedCriterionIndex) === grade.gradeLetter
                  );
                  const percentage = products.length > 0 ? (gradedProducts.length / products.length * 100).toFixed(1) : '0';

                  return (
                    <tr key={grade.gradeLetter}>
                      <td className="grade-cell">{grade.gradeLetter}</td>
                      <td>{gradedProducts.length}</td>
                      <td>{percentage}%</td>                      <td className="product-list-cell">
                        {gradedProducts.map((product, idx) => (
                          <span
                            key={idx}
                            className="product-name-chip"
                            onClick={() => openDetailModal(products.findIndex(p => p === product))}
                            onDoubleClick={() => openDetailModal(products.findIndex(p => p === product))}
                            title={t('gradeAssignment.distribution.doubleClickToView')}
                          >
                            {product.reviewTitle}
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>      <StepNavigation
        currentStep={4}
        canProceed={canFinish}
        onNext={handleNext}
        onPrev={handlePrev}
        nextLabel={t('gradeAssignment.nextButton')}
        prevLabel={t('gradeAssignment.prevButton')}
      />

      {/* 等級編集モーダル */}
      {isGradeEditModalOpen && (
        <div className="modal-overlay" onClick={() => setIsGradeEditModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('gradeAssignment.modal.title')}</h3>
              <button
                className="modal-close"
                onClick={() => setIsGradeEditModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* 等級名設定 */}
              <div className="grade-names">
                <h5 className="grade-names__title">{t('gradeAssignment.modal.gradeNamesTitle')}</h5>
                <div className="grade-name-inputs">
                  {gradeSettings.map((grade, index) => (
                    <div key={index} className="grade-name-input">
                      <Input
                        label={t('gradeAssignment.modal.gradeLabel', { number: index + 1 })}
                        value={grade.gradeLetter}
                        onChange={(e) => handleGradeLetterChange(index, e.target.value)}
                        placeholder={t('gradeAssignment.modal.gradePlaceholder')}
                      />
                      {gradeSettings.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<TrashIcon />}
                          onClick={() => removeGrade(index)}
                        >
                          {t('gradeAssignment.modal.deleteButton')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  variant="secondary"
                  leftIcon={<PlusIcon />}
                  onClick={addGrade}
                >
                  {t('gradeAssignment.modal.addGradeButton')}
                </Button>
              </div>
            </div>

            <div className="modal-footer">
              <Button
                variant="primary"
                onClick={() => setIsGradeEditModalOpen(false)}
              >
                {t('gradeAssignment.modal.doneButton')}
              </Button>
            </div>          </div>
        </div>
      )}

      {/* プレビュー用モーダル */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        url={previewModal.url}
        title={previewModal.title}
        onClose={closePreview}
      />

      {/* 編集モーダル */}
      {editingProductIndex !== null && (
        <EditProductModal
          isOpen={true}
          productIndex={editingProductIndex}
          onClose={() => setEditingProductIndex(null)}
          onNavigate={(direction) => {
            const newIndex = direction === 'prev' 
              ? Math.max(0, editingProductIndex - 1)
              : Math.min(products.length - 1, editingProductIndex + 1);
            setEditingProductIndex(newIndex);
          }}
        />
      )}
    </section>
  );
};